import { EmailRecipient } from "../types";
import {
  EmailType,
  FinancialReportOptions,
  NewsletterOptions,
  sendEmail as sendEmailService,
} from "../services/emailService";
import { logEmailSend } from "../services/loggingService";
import { auth } from "../utils/firebase";

/**
 * Send an email using the appropriate template and service
 *
 * @param user Email recipient with channels
 * @param content Email content (HTML)
 * @param greeting Email greeting
 * @param subject Email subject
 * @param month Month for financial reports
 * @param year Year for financial reports
 * @param emailType Type of email to send (financial or newsletter)
 * @param testing Whether this is a test email
 * @param testEmail Email address to use for testing
 * @returns Promise resolving to the send result
 */
export const sendEmail = async (
  user: EmailRecipient,
  content: string,
  greeting: string,
  subject: string,
  month: string = "",
  year: string = "",
  emailType: string = "financial", // Default to financial for backward compatibility
  testing: boolean = false,
  testEmail: string = ""
): Promise<any> => {
  let logId: string | null = null;
  
  // Get current user for logging (check both Firebase and AWS Cognito)
  let currentUser = auth.currentUser;
  let userUid = currentUser?.uid;
  
  if (!currentUser) {
    // Check for persisted AWS Cognito auth
    try {
      const persistedUser = localStorage.getItem('aws-cognito-user');
      if (persistedUser) {
        const userData = JSON.parse(persistedUser);
        if (userData.uid) {
          userUid = userData.uid;
          currentUser = { uid: userUid } as any; // Create a mock user object for compatibility
        }
      }
    } catch (error) {
      // Silently handle error checking AWS Cognito auth
    }
  }
  
  if (!userUid) {
    throw new Error("User not authenticated");
  }

  // Extract custom IDs from channels if available
  let customIds: string[] = [];
  if (user.channels) {
    for (let i = 0; i < user.channels.length; i++) {
      if (user.channels[i].customID) {
        customIds.push(user.channels[i].customID);
      }
    }
  }
  
  try {
    console.log(`Sending ${emailType} email to ${user.email}`);

    // Log email attempt as pending
    try {
      logId = await logEmailSend({
        recipientEmail: testing ? (testEmail || user.email) : user.email,
        subject,
        emailType: emailType as 'financial' | 'newsletter',
        status: 'pending',
        userId: userUid,
        channels: customIds,
        testMode: testing,
        month: emailType === 'financial' ? month : undefined,
        year: emailType === 'financial' ? year : undefined,
      });
    } catch (logError) {
      console.warn("Failed to log email attempt:", logError);
      // Continue with email sending even if logging fails
    }

    // Prepare recipient
    const recipients = [{ email: user.email }];
    console.log("Testing mode:", testing, "Test email:", testEmail);

    let result;

    // Determine which email type to send
    if (emailType === "financial") {
      // Financial report email
      const options: FinancialReportOptions = {
        recipients,
        subject,
        body: content,
        greeting,
        month,
        year,
        customIds: customIds.length > 0 ? customIds : undefined,
        testMode: testing,
        testEmail: testEmail || user.email,
      };

      result = await sendEmailService(
        EmailType.FINANCIAL_REPORT,
        options
      );
    } else {
      // Newsletter email
      const options: NewsletterOptions = {
        recipients,
        subject,
        body: content,
        greeting,
        customIds: customIds.length > 0 ? customIds : undefined,
        testMode: testing,
        testEmail: testEmail || user.email,
      };

      result = await sendEmailService(EmailType.NEWSLETTER, options);
    }

    // Update log status to sent on success
    if (logId) {
      try {
        await logEmailSend({
          recipientEmail: testing ? (testEmail || user.email) : user.email,
          subject,
          emailType: emailType as 'financial' | 'newsletter',
          status: 'sent',
          userId: userUid,
          channels: customIds,
          testMode: testing,
          month: emailType === 'financial' ? month : undefined,
          year: emailType === 'financial' ? year : undefined,
        });
      } catch (logError) {
        console.warn("Failed to update email log status:", logError);
      }
    }

    return result;
  } catch (error) {
    console.error("Error sending email:", error);

    // Update log status to failed on error
    if (logId && userUid) {
      try {
        await logEmailSend({
          recipientEmail: testing ? (testEmail || user.email) : user.email,
          subject,
          emailType: emailType as 'financial' | 'newsletter',
          status: 'failed',
          userId: userUid,
          channels: customIds,
          testMode: testing,
          month: emailType === 'financial' ? month : undefined,
          year: emailType === 'financial' ? year : undefined,
          error: error instanceof Error ? error.message : String(error),
        });
      } catch (logError) {
        console.warn("Failed to log email error:", logError);
      }
    }

    throw error;
  }
};
